// Painel de oscilografia: eixo X em segundos relativos (ao disparo ou ao início), um painel
// por unidade — como nos gráficos de tendência, nunca dois eixos no mesmo painel. Marcações
// verticais: o disparo (cor da marca) e cada evento do SOE (numerado, como na lista ao lado).
// O cursor é sincronizado entre os painéis; clicar num evento da lista posiciona o cursor.
//
// Zoom e cursores A/B como no TrendChart, mas controlados pela tela: a janela (`xRange`) e
// os cursores (`marks`) são os mesmos em todos os painéis. Com `areaZoom`, arrastar chama
// `onZoom(de, até)`; duplo clique chama `onReset`. As linhas A/B se arrastam em qualquer
// painel. O registro já veio inteiro: o zoom só recorta, não busca de novo.

import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { isDark, token } from "../lib/palette";
import { type Display, formatValue } from "../lib/units";

export interface OscSeries {
  tag: string;
  description: string;
  color: string;
  display: Display;
  values: number[];
}

export interface Marker {
  x: number;
  label: string;
}

export interface OscMarks {
  a: number | null;
  b: number | null;
}

/** Registro dos painéis montados, para a lista de eventos mover o cursor em todos. */
export type PanelRegistry = Set<uPlot>;

/** Índice da amostra mais próxima de `t` (as amostras são igualmente espaçadas). */
function indexAt(xs: number[], t: number): number | null {
  if (xs.length === 0) return null;
  if (xs.length === 1) return 0;
  const i = Math.round((t - xs[0]!) / (xs[1]! - xs[0]!));
  return Math.min(Math.max(i, 0), xs.length - 1);
}

export function OscPanel({
  title,
  xs,
  series,
  trigger,
  markers,
  highlight,
  syncKey,
  registry,
  onCursor,
  xRange,
  areaZoom,
  marks,
  onMark,
  onZoom,
  onReset,
}: {
  title: string;
  xs: number[];
  series: OscSeries[];
  trigger: number | null;
  markers: Marker[];
  highlight: number | null;
  syncKey: string;
  registry: PanelRegistry;
  onCursor: (x: number | null) => void;
  /** Trecho mostrado, em segundos relativos — o mesmo em todos os painéis. */
  xRange: [number, number];
  areaZoom: boolean;
  /** Cursores A/B (segundos relativos); null nos dois = desligados. */
  marks: OscMarks;
  onMark: (k: keyof OscMarks, x: number) => void;
  onZoom: (from: number, to: number) => void;
  onReset: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const [idx, setIdx] = useState<number | null>(null);
  const [themeTick, setThemeTick] = useState(0);
  // O uPlot guarda as funções e o estado do momento da criação; estes refs dão o atual.
  const draw = useRef({ trigger, markers, highlight, marks });
  draw.current = { trigger, markers, highlight, marks };
  const cb = useRef({ onCursor, onMark, onZoom, onReset });
  cb.current = { onCursor, onMark, onZoom, onReset };
  const xRangeRef = useRef(xRange);
  xRangeRef.current = xRange;
  const areaZoomRef = useRef(areaZoom);
  areaZoomRef.current = areaZoom;

  const data = useMemo(
    () => [xs, ...series.map((s) => s.values.map((v) => v / s.display.factor))] as uPlot.AlignedData,
    [xs, series],
  );

  // Mínimo e máximo do trecho mostrado (SI).
  const [i0, i1] = useMemo(() => [indexAt(xs, xRange[0]) ?? 0, indexAt(xs, xRange[1]) ?? -1], [xs, xRange]);
  const stats = useMemo(
    () =>
      series.map((s) => {
        let min = Infinity;
        let max = -Infinity;
        for (let k = i0; k <= i1; k++) {
          const v = s.values[k];
          if (v === undefined) continue;
          if (v < min) min = v;
          if (v > max) max = v;
        }
        return min <= max ? { min, max } : null;
      }),
    [series, i0, i1],
  );

  // O canvas não lê CSS: ao trocar o tema (navegador ou forçado pelo grupo), redesenha.
  useEffect(() => {
    const on = () => setThemeTick((t) => t + 1);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", on);
    const mo = new MutationObserver(on);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      mq.removeEventListener("change", on);
      mo.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const muted = token("--muted");
    const fg = token("--fg");
    const brand = token("--brand");
    const surface = token("--surface");
    const grid = isDark() ? "#2b3441" : "#e8ebef";
    const d0 = series[0]?.display;
    const font = "11px Inter, system-ui, sans-serif";
    const pr = uPlot.pxRatio;

    const u = new uPlot(
      {
        width: el.clientWidth,
        height: 200,
        legend: { show: false },
        // Lê o ref: a janela muda (zoom) sem recriar o gráfico.
        scales: { x: { time: false, range: () => xRangeRef.current } },
        series: [{}, ...series.map((s) => ({ label: s.description, stroke: s.color, width: 2, points: { show: false } }))],
        cursor: {
          sync: { key: syncKey, scales: ["x", null] },
          points: { size: 8, width: 2, stroke: surface },
          // Zoom por área só no tempo; quem aplica é a tela, nos painéis todos.
          drag: { x: true, y: false, setScale: false },
        },
        select: { show: true, left: 0, top: 0, width: 0, height: 0 },
        axes: [
          {
            stroke: muted,
            font,
            grid: { stroke: grid, width: 1 },
            ticks: { stroke: grid, width: 1 },
            values: (_u, splits) => splits.map((s) => `${s.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} s`),
          },
          {
            stroke: muted,
            font,
            size: 64,
            grid: { stroke: grid, width: 1 },
            ticks: { show: false },
            values: (_u, vals) => vals.map((v) => (d0 ? formatValue(v * d0.factor, { ...d0, decimals: Math.min(d0.decimals, 2) }) : String(v))),
          },
        ],
        hooks: {
          setCursor: [
            (uu) => {
              const i = uu.cursor.idx ?? null;
              setIdx(i);
              cb.current.onCursor(i === null ? null : (xs[i] ?? null));
            },
          ],
          setSelect: [
            (uu) => {
              if (uu.select.width < 5) return;
              const from = uu.posToVal(uu.select.left, "x");
              const to = uu.posToVal(uu.select.left + uu.select.width, "x");
              uu.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
              cb.current.onZoom(from, to);
            },
          ],
          draw: [
            (uu) => {
              const { ctx } = uu;
              const { top, height } = uu.bbox;
              const visible = (px: number) => px >= uu.bbox.left && px <= uu.bbox.left + uu.bbox.width;
              const line = (x: number, color: string, width: number) => {
                const px = Math.round(uu.valToPos(x, "x", true)) + 0.5;
                ctx.save();
                ctx.strokeStyle = color;
                ctx.lineWidth = width * pr;
                ctx.beginPath();
                ctx.moveTo(px, top);
                ctx.lineTo(px, top + height);
                ctx.stroke();
                ctx.restore();
                return px;
              };
              const label = (px: number, text: string, color: string) => {
                ctx.save();
                ctx.font = `${11 * pr}px Inter, system-ui, sans-serif`;
                ctx.fillStyle = color;
                ctx.fillText(text, px + 3 * pr, top + 12 * pr);
                ctx.restore();
              };
              const { trigger: tr, markers: mk, highlight: hl, marks: ab } = draw.current;
              ctx.save();
              // Fora do trecho mostrado, as marcações não invadem os eixos.
              ctx.beginPath();
              ctx.rect(uu.bbox.left, top, uu.bbox.width, height);
              ctx.clip();
              for (const [i, m] of mk.entries()) {
                const on = hl === i;
                const px = line(m.x, on ? fg : muted, on ? 2 : 1);
                label(px, m.label, on ? fg : muted);
              }
              if (tr !== null) {
                const px = line(tr, brand, 2);
                label(px, "disparo", brand);
              }
              ctx.restore();
              // Cursores A/B: tracejado na cor do texto, com a letra embaixo (em cima ficam
              // os números dos eventos).
              for (const [name, t] of [["A", ab.a], ["B", ab.b]] as const) {
                if (t === null) continue;
                const x = Math.round(uu.valToPos(t, "x", true));
                if (!visible(x)) continue;
                ctx.save();
                ctx.strokeStyle = fg;
                ctx.lineWidth = 1.5 * pr;
                ctx.setLineDash([5 * pr, 4 * pr]);
                ctx.beginPath();
                ctx.moveTo(x, top);
                ctx.lineTo(x, top + height);
                ctx.stroke();
                ctx.setLineDash([]);
                const w = 16 * pr;
                ctx.fillStyle = fg;
                ctx.fillRect(x - w / 2, top + height - w, w, w);
                ctx.fillStyle = surface;
                ctx.font = `bold ${11 * pr}px Inter, system-ui, sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(name, x, top + height - w / 2 + pr);
                ctx.restore();
              }
            },
          ],
        },
      },
      data,
      el,
    );
    plot.current = u;
    registry.add(u);

    const dbl = () => cb.current.onReset();
    // Cursores A/B: pegar a linha (±8 px) e arrastar. O mousedown é capturado no root, antes
    // de chegar ao `over`, para o uPlot não começar o zoom por área.
    let dragging: keyof OscMarks | null = null;
    const hit = (clientX: number): keyof OscMarks | null => {
      const x = clientX - u.over.getBoundingClientRect().left;
      let best: keyof OscMarks | null = null;
      let dist = 8;
      for (const k of ["a", "b"] as const) {
        const t = draw.current.marks[k];
        if (t === null) continue;
        const d = Math.abs(u.valToPos(t, "x") - x);
        if (d < dist) {
          dist = d;
          best = k;
        }
      }
      return best;
    };
    const move = (e: MouseEvent) => {
      if (!dragging) return;
      const r = u.over.getBoundingClientRect();
      const x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
      cb.current.onMark(dragging, u.posToVal(x, "x"));
    };
    const up = () => {
      dragging = null;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    const down = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const k = hit(e.clientX);
      if (!k) {
        // Sem o modo de zoom por área, o uPlot nem fica sabendo do mousedown.
        if (!areaZoomRef.current) e.stopPropagation();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      dragging = k;
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
    };
    const hover = (e: MouseEvent) => {
      if (!dragging) u.over.style.cursor = hit(e.clientX) ? "ew-resize" : areaZoomRef.current ? "zoom-in" : "";
    };
    u.over.addEventListener("dblclick", dbl);
    u.root.addEventListener("mousedown", down, true);
    u.over.addEventListener("mousemove", hover);
    const ro = new ResizeObserver(() => u.setSize({ width: el.clientWidth, height: 200 }));
    ro.observe(el);
    return () => {
      ro.disconnect();
      up();
      u.over.removeEventListener("dblclick", dbl);
      u.root.removeEventListener("mousedown", down, true);
      u.over.removeEventListener("mousemove", hover);
      registry.delete(u);
      u.destroy();
      plot.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, themeTick, syncKey]);

  // Zoom: só reescala.
  useEffect(() => {
    plot.current?.setScale("x", { min: xRange[0], max: xRange[1] });
  }, [xRange]);

  // Marcações novas (destaque ao clicar na lista, cursores A/B) só pedem redesenho.
  useEffect(() => {
    plot.current?.redraw(false, false);
  }, [trigger, markers, highlight, marks]);

  const showMarks = marks.a !== null && marks.b !== null;
  const ia = marks.a !== null ? indexAt(xs, marks.a) : null;
  const ib = marks.b !== null ? indexAt(xs, marks.b) : null;
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-baseline justify-between gap-2 px-4 pt-3">
        <h3 className="text-sm font-medium">{title}</h3>
        <span className="text-xs text-muted tabular-nums">
          {idx !== null && xs[idx] !== undefined ? `t = ${xs[idx]!.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} s` : "passe o cursor sobre o gráfico"}
        </span>
      </div>
      <div ref={box} className="w-full px-1" />
      {/* Canais: identidade nunca só pela cor (nome + unidade). Mín/máx são do trecho mostrado. */}
      <div className="overflow-x-auto border-t border-line">
        <table className="w-full text-xs">
          <thead className="text-muted">
            <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-1 [&>th]:font-normal">
              <th className="text-left">Canal</th>
              <th className="text-right">Cursor</th>
              <th className="text-right">Mínimo</th>
              <th className="text-right">Máximo</th>
              {showMarks && (
                <>
                  <th className="border-l border-line text-right">A</th>
                  <th className="text-right">B</th>
                  <th className="text-right">Δ (B − A)</th>
                </>
              )}
              <th className="text-left">Unid.</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s, k) => {
              const f = (x: number | null | undefined) => (x === null || x === undefined ? "—" : formatValue(x, s.display));
              const v = idx !== null ? s.values[idx] : null;
              const va = ia !== null ? s.values[ia] : null;
              const vb = ib !== null ? s.values[ib] : null;
              const delta = va !== null && va !== undefined && vb !== null && vb !== undefined ? vb - va : null;
              return (
                <tr key={s.tag} className="border-t border-line [&>td]:whitespace-nowrap [&>td]:px-2 [&>td]:py-1">
                  <td className="text-left">
                    <span className="flex items-center gap-2">
                      <span className="h-0.5 w-4 shrink-0 rounded-full" style={{ background: s.color }} aria-hidden />
                      {s.description}
                    </span>
                  </td>
                  <td className="text-right font-medium tabular-nums">{f(v)}</td>
                  <td className="text-right tabular-nums">{f(stats[k]?.min)}</td>
                  <td className="text-right tabular-nums">{f(stats[k]?.max)}</td>
                  {showMarks && (
                    <>
                      <td className="border-l border-line text-right tabular-nums">{f(va)}</td>
                      <td className="text-right tabular-nums">{f(vb)}</td>
                      <td className="text-right font-medium tabular-nums">{delta === null ? "—" : `${delta > 0 ? "+" : ""}${formatValue(delta, s.display)}`}</td>
                    </>
                  )}
                  <td className="text-left text-muted">{s.display.unit}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
